import React from "react";
import {Button, Col, Modal, Row} from "react-bootstrap";
import * as _ from 'lodash';
import useModalKeyboardNavigation from '../../utilities/hooks/useModalKeyboardNavigation';

const formatAddress = address => {
    if (!address) return "";

    return _.compact([
        address.address_1,
        address.address_2,
        address.city,
        address.state,
        address.zip,
        address.country
    ]).join(", ");
};

const AddressMismatchModal = ({mismatches = [], onAccept, onHide, isSaving, savingMismatchKey}) => {
    useModalKeyboardNavigation({
        modalId: "address-mismatch-modal",
        enabled: true,
        dependencies: [mismatches, isSaving, savingMismatchKey]
    });

    const canAcceptMismatch = (mismatch) => {
        return !!(
            ["site", "customer"].includes(mismatch.record_type) && mismatch.quickbooks_id
        );
    };

    const hasPendingCustomerMismatch = mismatches.some(
        mismatch => mismatch.record_type === "customer" && canAcceptMismatch(mismatch)
    );

    return (
        <Modal
            id="address-mismatch-modal"
            show={true}
            onHide={onHide}
            backdrop
            bsSize="large"
            className="heightAuto fontIOS preserve-text-area-format"
        >
            <Modal.Header closeButton>
                <Modal.Title>Address mismatch</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {mismatches.map((mismatch, mismatchIndex) => {
                    const mismatchKey = mismatch?._modalKey || `mismatch-${mismatchIndex}`;
                    const blockedByCustomer = mismatch.record_type === "site" && hasPendingCustomerMismatch;
                    const savingThisMismatch = isSaving && savingMismatchKey === mismatchKey;
                    const detectedMismatches = mismatch.mismatches || [];

                    return (
                        <div key={mismatchKey} className={mismatchIndex > 0 ? "margin-top-20" : ""}>
                            <Row>
                                <Col xs={8}>
                                    <h5>
                                        {mismatch?.record_type === "customer" ? "Customer" : mismatch?.record_type === "site" ? "Site" : "Record"} address
                                    </h5>
                                </Col>
                                <Col xs={4} className="text-right">
                                    <Button
                                        bsSize="small"
                                        bsStyle="success"
                                        onClick={() => onAccept(mismatch)}
                                        disabled={isSaving || !canAcceptMismatch(mismatch) || blockedByCustomer}
                                    >
                                        {savingThisMismatch ? "Saving..." : "Accept proposed address"}
                                    </Button>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <h5>Current address</h5>
                                    <p>{formatAddress(mismatch.current_address)}</p>
                                </Col>
                                <Col xs={6}>
                                    <h5>Proposed address</h5>
                                    <p>{formatAddress(mismatch.proposed_address)}</p>
                                </Col>
                            </Row>
                            {detectedMismatches.length > 0 && (
                                <div>
                                    <h5>Detected mismatches</h5>
                                    {detectedMismatches.map((item, i) => (
                                        <Row key={`${item.field}-${i}`}>
                                            <Col xs={1} className="text-right">{i + 1}.</Col>
                                            <Col xs={3}>{item.field}</Col>
                                            <Col xs={4}>Current: {item.got}</Col>
                                            <Col xs={4}>Proposed: {item.expected}</Col>
                                        </Row>
                                    ))}
                                </div>
                            )}
                            {blockedByCustomer && (
                                <p className="text-muted">Accept customer address first to enable site sync.</p>
                            )}
                            {mismatchIndex < mismatches.length - 1 && <hr/>}
                        </div>
                    );
                })}
            </Modal.Body>
            <Modal.Footer>
                <Button bsSize="small" onClick={onHide} disabled={isSaving}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddressMismatchModal;
